// Client for the groups API at groups.haikubox.com, used by GroupDashboard.vue.
// Full reference: https://groups.haikubox.com/docs
//
// Every call is scoped to one group. If the group has an access key, calls
// send it as the x-api-key header. The key comes from a share link
// (#/groups/<slug>?key=...) or the key prompt, and is kept in localStorage
// per slug. It's never put in a request URL, because API Gateway logs query strings.

export const GROUPS_API_BASE = "https://groups.haikubox.com";

// The group requires an access key and the stored one is missing or wrong.
export class KeyRequiredError extends Error {
  constructor() {
    super("Key required.");
    this.name = "KeyRequiredError";
  }
}

// Unknown group, box not in the group, or a detection that isn't available.
// The API deliberately doesn't say which.
export class NotFoundError extends Error {
  constructor() {
    super("Not found.");
    this.name = "NotFoundError";
  }
}

const KEY_PREFIX = "groupKey:";

export function getStoredKey(slug) {
  try {
    return localStorage.getItem(KEY_PREFIX + slug) || null;
  } catch (e) {
    return null;
  }
}

export function storeKey(slug, key) {
  try {
    if (key) localStorage.setItem(KEY_PREFIX + slug, key);
    else localStorage.removeItem(KEY_PREFIX + slug);
  } catch (e) {
    // Private browsing etc. The key then lasts only as long as the page.
  }
}

async function get(slug, path, params = {}) {
  const url = new URL(GROUPS_API_BASE + path);
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(name, value);
    }
  }
  // Only keyed groups send the header, so open groups make simple requests
  // with no CORS preflight.
  const key = getStoredKey(slug);
  const response = await fetch(url.toString(), key ? { headers: { "x-api-key": key } } : {});
  if (response.status === 401) throw new KeyRequiredError();
  if (response.status === 404) throw new NotFoundError();
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Groups API request failed: ${response.status}`);
  }
  return body;
}

const enc = encodeURIComponent;

// { groupSlug, groupName, boxes: [{serial, name, timezone, active24h}],
//   yesterday: {detections, species, topSpecies: [{cn, spCode, count}]} }
export function getSummary(slug) {
  return get(slug, `/groups/${enc(slug)}/summary`);
}

// { serial, date, timezone, truncated, detections: [{id, dt, cn, sn, spCode,
//   hasAudio, level: "L"|"M"|"H", score, v3Score?, detectionCount?}] }
// date is the box's local day (YYYY-MM-DD), within the last 30 days.
export function getDetections(slug, serial, date) {
  return get(slug, `/groups/${enc(slug)}/${enc(serial)}/detections`, { date });
}

// { id, serial, dt, cn, offset, url }. The url is presigned for 1 hour.
// download: true gives a URL the browser saves instead of plays.
export function getAudio(slug, serial, id, { download = false } = {}) {
  return get(slug, `/groups/${enc(slug)}/${enc(serial)}/audio`, {
    id,
    download: download ? "1" : undefined,
  });
}

// { from, to, timezone, species: [{cn, spCode, days: [[date, count, low, med, high]]}] }
// weeks 1-52. low/med/high are null on days from older firmware, which
// doesn't record the split; count is then the total at all levels.
export function getDailyCounts(slug, serial, { weeks = 1, cn } = {}) {
  return get(slug, `/groups/${enc(slug)}/${enc(serial)}/daily-counts`, { weeks, cn });
}
